import type { ReactNode } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useMetaTags } from "../components/MetaTags";
import type { Tool } from "./tools";
import { getTool } from "./tools";
import { withToolAttribution } from "../lib/toolAttribution";
import { ToolSlugProvider } from "./ToolSlugContext";

const BASE = "https://omnicanvasnotes.com";

// Tool copy links to sibling tools and guides with Markdown-style
// `[text](/path)`. Render those as real links -- as plain text they showed the
// brackets on the page and gave search engines no internal link. Only site
// paths are linked; anything else stays text.
const INLINE_LINK = /\[([^\]]+)\]\((\/[^)\s]*)\)/g;

export function renderInlineLinks(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE_LINK)) {
    const index = match.index ?? 0;
    if (index > last) out.push(text.slice(last, index));
    out.push(
      <a key={index} href={match[2]} className="text-indigo-600 underline decoration-indigo-200 underline-offset-2 hover:decoration-indigo-500">
        {match[1]}
      </a>,
    );
    last = index + match[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function jsonLd(tool: Tool): string {
  const url = `${BASE}/tools/${tool.slug}`;
  const graph = [
    {
      "@type": "SoftwareApplication",
      name: tool.name,
      description: tool.description,
      applicationCategory: "WebApplication",
      operatingSystem: "Any (web browser)",
      url,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "OmniCanvas" },
    },
    {
      "@type": "FAQPage",
      mainEntity: tool.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Free Tools", item: `${BASE}/tools` },
        { "@type": "ListItem", position: 2, name: tool.name, item: url },
      ],
    },
  ];
  return JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
}

export function ToolLayout({ tool, children }: { tool: Tool; children: ReactNode }) {
  useMetaTags({
    title: tool.title,
    description: tool.description,
    url: `${BASE}/tools/${tool.slug}`,
    ogImage: tool.ogImage, // undefined → site default
  });

  const related = tool.related.map(getTool).filter(Boolean) as Tool[];

  return (
    <ToolSlugProvider value={tool.slug}>
    <main className="bg-white pt-16">
      {/* JSON-LD for rich results (rendered into SSR markup) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLd(tool) }}
      />

      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-6 pt-8" data-no-print>
        <nav className="flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
          <a href="/" className="hover:text-slate-700 transition-colors">Home</a>
          <ChevronRight size={14} />
          <a href="/tools" className="hover:text-slate-700 transition-colors">Free Tools</a>
          <ChevronRight size={14} />
          <span className="text-slate-600">{tool.name}</span>
        </nav>
      </div>

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-6 pt-6 pb-8" data-no-print>
        <span className="inline-block text-xs font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 rounded-full px-3 py-1 mb-4">
          {tool.category} · Free Tool
        </span>
        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-4">
          {tool.name}
        </h1>
        <p className="text-lg md:text-xl text-slate-500 max-w-2xl">{tool.tagline}</p>
      </header>

      {/* Interactive widget */}
      <section className="max-w-5xl mx-auto px-6">{children}</section>

      {/* Lede / intro */}
      <section className="max-w-3xl mx-auto px-6 pt-12" data-no-print>
        {tool.intro.map((p, i) => (
          <p key={i} className="text-slate-600 leading-relaxed mb-4 text-[1.05rem]">
            {renderInlineLinks(p)}
          </p>
        ))}
      </section>

      {/* Primary CTA band */}
      <section className="max-w-5xl mx-auto px-6 py-12" data-no-print>
        <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-3xl px-8 py-12 md:px-12 text-center shadow-xl shadow-indigo-100">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">{tool.ctaTitle}</h2>
          <p className="text-indigo-100 max-w-xl mx-auto mb-7">{tool.ctaText}</p>
          <a
            href={withToolAttribution(tool.appHref, tool.slug, "hero_cta")}
            className="group inline-flex items-center gap-2.5 bg-white text-indigo-700 font-bold px-8 py-3.5 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
          >
            Sign up free
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
          <p className="text-indigo-200 text-xs mt-4">No credit card · 30 days free</p>
        </div>
      </section>

      {/* Editorial sections */}
      <article className="max-w-3xl mx-auto px-6 pb-4" data-no-print>
        {tool.sections.map((s, i) => (
          <section key={i} className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{s.h2}</h2>
            {s.body.map((p, j) => (
              <p key={j} className="text-slate-600 leading-relaxed mb-4">
                {renderInlineLinks(p)}
              </p>
            ))}
          </section>
        ))}
      </article>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-8" data-no-print>
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently asked questions</h2>
        <div className="divide-y divide-slate-100">
          {tool.faqs.map((f, i) => (
            <div key={i} className="py-5">
              <h3 className="font-semibold text-slate-900 mb-2">{f.q}</h3>
              <p className="text-slate-600 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Related tools */}
      {related.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-12 border-t border-slate-100" data-no-print>
          <h2 className="text-xl font-bold text-slate-900 mb-6">More free tools</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {related.map((r) => (
              <a
                key={r.slug}
                href={`/tools/${r.slug}`}
                className="group block rounded-2xl border border-slate-200 p-5 hover:border-indigo-300 hover:shadow-md transition-all"
              >
                <p className="font-semibold text-slate-900 group-hover:text-indigo-700 transition-colors mb-1">
                  {r.name}
                </p>
                <p className="text-sm text-slate-500">{r.blurb}</p>
              </a>
            ))}
          </div>
          <div className="mt-6">
            <a
              href="/tools"
              className="inline-flex items-center gap-1.5 text-indigo-600 font-medium text-sm hover:text-indigo-800 transition-colors"
            >
              See all free tools
              <ArrowRight size={14} />
            </a>
          </div>
        </section>
      )}
    </main>
    </ToolSlugProvider>
  );
}
